import React, { useState, useEffect } from "react";

// --- EXAMPLE DATA ---
// REMOVED: exampleJsonInputArray is no longer used here, data will be in public/investments/*.json

// --- COMPONENT ---
const LpPerformanceTracker = () => {
  // REMOVED: const [jsonInput, setJsonInput] = useState(...);
  const [availableInvestmentFiles, setAvailableInvestmentFiles] = useState([]);
  const [selectedInvestmentFile, setSelectedInvestmentFile] = useState("");
  const [calculatedResults, setCalculatedResults] = useState([]);
  const [categorySummaries, setCategorySummaries] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to format duration
  const formatDuration = (ms) => {
    if (ms < 0) ms = 0;
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));

    const parts = [];
    if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
    if (hours > 0) parts.push(`${hours} hour${hours > 1 ? "s" : ""}`);
    if (minutes > 0 && days === 0)
      parts.push(`${minutes} minute${minutes > 1 ? "s" : ""}`); // Only show minutes if duration is less than a day

    if (parts.length === 0) {
      if (ms < 60000) return `< 1 minute ago`; // Less than a minute
      return `${seconds} second${seconds !== 1 ? "s" : ""} ago`; // Should ideally not hit this if minutes are shown
    }

    return parts.slice(0, 2).join(", ") + " ago"; // Show up to two most significant units
  };

  useEffect(() => {
    // Read investment files from environment variable
    const filesFromEnv = process.env.REACT_APP_INVESTMENT_FILES;
    if (filesFromEnv) {
      const fileList = filesFromEnv
        .split(",")
        .map((f) => f.trim())
        .filter((f) => f.endsWith(".json"));
      if (fileList.length > 0) {
        setAvailableInvestmentFiles(fileList);
        setSelectedInvestmentFile(fileList[0]); // Select the first file by default
      } else {
        setError(
          "No valid .json files found in REACT_APP_INVESTMENT_FILES environment variable."
        );
        setAvailableInvestmentFiles([]);
      }
    } else {
      setError(
        "REACT_APP_INVESTMENT_FILES environment variable is not set or is empty. Please define it in your .env file (e.g., REACT_APP_INVESTMENT_FILES='file1.json,file2.json')."
      );
      setAvailableInvestmentFiles([]);
      console.warn(
        "REACT_APP_INVESTMENT_FILES is not set. Frontend won't list investment files."
      );
    }
    setLoading(false);
  }, []);

  // Fetches necessary data from Balancer's GraphQL API
  const fetchBalancerPoolData = async (poolId, userAddress, chain) => {
    const query = `query GetPool($id: String!, $chain: GqlChain!, $userAddress: String) {
          pool: poolGetPool(id: $id, chain: $chain, userAddress: $userAddress) {
            userBalance { totalBalanceUsd }
            poolTokens { address symbol priceRate }
          }
        }`;
    const variables = { id: poolId, chain, userAddress };
    try {
      const response = await fetch("https://api-v3.balancer.fi/graphql", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operationName: "GetPool", query, variables }),
      });
      if (!response.ok)
        throw new Error(
          `GraphQL request failed for pool ${poolId}: ${response.statusText}`
        );
      const jsonResponse = await response.json();
      if (jsonResponse.errors)
        throw new Error(
          `GraphQL error for pool ${poolId}: ${JSON.stringify(
            jsonResponse.errors
          )}`
        );
      if (
        !jsonResponse.data ||
        !jsonResponse.data.pool ||
        !jsonResponse.data.pool.userBalance ||
        !jsonResponse.data.pool.poolTokens
      ) {
        // For fictional pools in example, this might be hit. Handle gracefully.
        console.warn(
          `Incomplete or missing pool data for ${poolId}. This might be a fictional pool in the example.`
        );
        // Return a structure that won't break parseFloat, or handle upstream
        return { userBalance: { totalBalanceUsd: "0" }, poolTokens: [] };
      }
      return jsonResponse.data.pool;
    } catch (e) {
      console.error(`Failed to fetch Balancer pool data for ${poolId}:`, e);
      // Return a mock structure if fetch fails for an example pool to avoid breaking all calcs
      if (
        poolId.includes("fictional") ||
        poolId.includes("anotherPoolAddress") ||
        poolId.includes("simPoolAddress")
      ) {
        console.warn(
          `Returning mock data for fictional/failed pool fetch: ${poolId}`
        );
        return { userBalance: { totalBalanceUsd: "0" }, poolTokens: [] };
      }
      throw e;
    }
  };

  const calculateSinglePerformance = async (investment) => {
    const poolData = await fetchBalancerPoolData(
      investment.poolAddress,
      investment.userLpAddress,
      investment.chain
    );

    let currentLpValue = 0;
    if (poolData.userBalance && poolData.userBalance.totalBalanceUsd) {
      currentLpValue = parseFloat(poolData.userBalance.totalBalanceUsd);
    }
    if (isNaN(currentLpValue)) currentLpValue = 0; // Default if API fails or data is bad

    const initialToken0 = investment.token0;
    const initialToken1 = investment.token1;

    // Mock current prices if poolTokens is empty (e.g. for fictional pools)
    const getPrice = (tokenAddress, poolTokens, fallbackPrice = 1) => {
      const tokenData = poolTokens.find(
        (pt) => pt.address.toLowerCase() === tokenAddress.toLowerCase()
      );
      return tokenData && tokenData.priceRate
        ? parseFloat(tokenData.priceRate)
        : fallbackPrice;
    };

    const currentToken0Price = getPrice(
      initialToken0.address,
      poolData.poolTokens,
      initialToken0.price
    ); // Fallback to initial for mock
    const currentToken1Price = getPrice(
      initialToken1.address,
      poolData.poolTokens,
      initialToken1.price
    ); // Fallback to initial for mock

    const initialValueToken0 = initialToken0.amount * initialToken0.price;
    const initialValueToken1 = initialToken1.amount * initialToken1.price;
    const initialTotalValueUsd = initialValueToken0 + initialValueToken1;

    if (
      initialTotalValueUsd <= 0 &&
      investment.poolAddress !== "0xanotherPoolAddressForDVK" &&
      investment.poolAddress !== "0xsimPoolAddressHere"
    ) {
      // Allow zero for mock pools
      console.warn(
        `Initial total value for investment ID ${
          investment.id
        } is zero or negative ($${initialTotalValueUsd.toFixed(2)}).`
      );
      // Return a structure that won't break aggregation for real pools, mock pools are fine
    }

    const hodlToken0Value =
      initialTotalValueUsd > 0
        ? (initialTotalValueUsd / initialToken0.price) * currentToken0Price
        : 0;
    const hodlToken1Value =
      initialTotalValueUsd > 0
        ? (initialTotalValueUsd / initialToken1.price) * currentToken1Price
        : 0;
    const hodlOriginalBasketValue =
      initialToken0.amount * currentToken0Price +
      initialToken1.amount * currentToken1Price;

    const netGainFromLp = currentLpValue - hodlOriginalBasketValue;

    const now = Date.now();
    const depositTimestampMs = investment.depositTimestamp * 1000;
    const durationMs = now - depositTimestampMs;
    let apr = NaN;
    let apy = NaN;
    let isSubDayApyExtrapolation = false;

    if (durationMs > 0 && initialTotalValueUsd > 0) {
      const rateOfReturn = currentLpValue / initialTotalValueUsd - 1;
      const millisecondsInYear = 365.25 * 24 * 60 * 60 * 1000;
      apr = (rateOfReturn / durationMs) * millisecondsInYear * 100;
      const days = durationMs / (1000 * 60 * 60 * 24);
      const dailyEquivalentRate =
        Math.pow(currentLpValue / initialTotalValueUsd, 1 / days) - 1;
      if (isFinite(dailyEquivalentRate) && dailyEquivalentRate > -100) {
        apy = (Math.pow(1 + dailyEquivalentRate, 365.25) - 1) * 100;
        if (days < 1) {
          isSubDayApyExtrapolation = true;
        }
      }
    } else if (initialTotalValueUsd === 0 && currentLpValue > 0) {
      // Handle case where initial value was 0 but current is positive (infinite APR/APY essentially)
      apr = Infinity;
      apy = Infinity;
    }

    const strategies = [
      { name: "LP Position", value: currentLpValue },
      {
        name: `HODL ${initialToken0.symbol || "Token0"}`,
        value: hodlToken0Value,
      },
      {
        name: `HODL ${initialToken1.symbol || "Token1"}`,
        value: hodlToken1Value,
      },
      { name: "HODL Original Basket", value: hodlOriginalBasketValue },
    ];

    const validStrategies = strategies.filter(
      (s) => !isNaN(s.value) && isFinite(s.value)
    );
    let bestPerformingStrategy = { name: "N/A", value: 0, pnlVsLp: 0 };
    if (validStrategies.length > 0) {
      validStrategies.sort((a, b) => b.value - a.value);
      bestPerformingStrategy = validStrategies[0];
    }

    const allStrategiesCompared = strategies.map((s) => ({
      name: s.name,
      value: s.value,
      pnlVsLp:
        isNaN(s.value) || isNaN(currentLpValue)
          ? NaN
          : s.value - currentLpValue,
    }));

    // --- ETH Denominated Performance ---
    let initialLpWorthInEth = NaN; // Renamed from initialEthAmountInPool for clarity
    let initialEthPriceUsd = NaN;
    let currentEthPriceUsd = NaN;
    let ethSymbolInPool = "";
    let lpUsdValueAsEthAtInitialPrice = NaN;
    let lpValueAsEthAtInitialPrice_pctDiff = NaN; // New percentage field
    let lpUsdValueAsEthAtCurrentPrice = NaN;
    let lpValueAsEthAtCurrentPrice_pctDiff = NaN; // New percentage field
    let netGrowthInEthToken = NaN;
    let netGrowthInEthToken_pct = NaN; // New percentage field

    const checkSymbol = (symbol) =>
      symbol?.toUpperCase() === "ETH" || symbol?.toUpperCase() === "WETH";

    if (checkSymbol(initialToken0.symbol)) {
      // initialEthAmountInPool = initialToken0.amount; // No longer tracking raw ETH amount here for this perspective
      initialEthPriceUsd = initialToken0.price;
      currentEthPriceUsd = currentToken0Price;
      ethSymbolInPool = initialToken0.symbol;
    } else if (checkSymbol(initialToken1.symbol)) {
      // initialEthAmountInPool = initialToken1.amount; // No longer tracking raw ETH amount here for this perspective
      initialEthPriceUsd = initialToken1.price;
      currentEthPriceUsd = currentToken1Price;
      ethSymbolInPool = initialToken1.symbol;
    }

    if (
      ethSymbolInPool &&
      !isNaN(currentLpValue) &&
      !isNaN(initialTotalValueUsd)
    ) {
      if (!isNaN(initialEthPriceUsd) && initialEthPriceUsd > 0) {
        initialLpWorthInEth = initialTotalValueUsd / initialEthPriceUsd; // LP value in ETH at deposit
        lpUsdValueAsEthAtInitialPrice = currentLpValue / initialEthPriceUsd;
        if (initialLpWorthInEth !== 0 && !isNaN(initialLpWorthInEth)) {
          lpValueAsEthAtInitialPrice_pctDiff =
            (lpUsdValueAsEthAtInitialPrice / initialLpWorthInEth - 1) * 100;
        }
      }
      if (!isNaN(currentEthPriceUsd) && currentEthPriceUsd > 0) {
        lpUsdValueAsEthAtCurrentPrice = currentLpValue / currentEthPriceUsd;
        if (initialLpWorthInEth !== 0 && !isNaN(initialLpWorthInEth)) {
          // Check against the calculated initial LP worth in ETH
          netGrowthInEthToken =
            lpUsdValueAsEthAtCurrentPrice - initialLpWorthInEth;
          netGrowthInEthToken_pct =
            (netGrowthInEthToken / initialLpWorthInEth) * 100;
          lpValueAsEthAtCurrentPrice_pctDiff =
            (lpUsdValueAsEthAtCurrentPrice / initialLpWorthInEth - 1) * 100;
        }
      }
    }
    // --- End ETH Denominated Performance ---

    return {
      investmentDetails: investment, // Includes category now
      currentLpValue,
      hodlToken0Value,
      hodlToken1Value,
      hodlOriginalBasketValue,
      netGainFromLp,
      apr,
      apy,
      isSubDayApyExtrapolation,
      initialTotalValueUsd, // Needed for category sum
      bestStrategy: {
        name: bestPerformingStrategy.name,
        value: bestPerformingStrategy.value,
        pnlVsLp: bestPerformingStrategy.value - currentLpValue,
      },
      allStrategiesCompared,
      // ETH Denominated fields
      initialLpWorthInEth, // Changed from initialEthAmountInPool
      initialEthPriceUsd,
      currentEthPriceUsd,
      ethSymbolInPool,
      lpUsdValueAsEthAtInitialPrice,
      lpValueAsEthAtInitialPrice_pctDiff, // Added
      lpUsdValueAsEthAtCurrentPrice,
      lpValueAsEthAtCurrentPrice_pctDiff, // Added
      netGrowthInEthToken,
      netGrowthInEthToken_pct, // Added
    };
  };

  const isValidInvestment = (obj) => {
    return (
      obj &&
      typeof obj.id === "string" &&
      typeof obj.category === "string" &&
      obj.category.length > 0 && // Validate category
      typeof obj.poolAddress === "string" &&
      typeof obj.userLpAddress === "string" &&
      typeof obj.chain === "string" &&
      obj.chain.length > 0 && // Validate chain
      obj.token0 &&
      typeof obj.token0.address === "string" &&
      typeof obj.token0.amount === "number" &&
      typeof obj.token0.price === "number" &&
      obj.token1 &&
      typeof obj.token1.address === "string" &&
      typeof obj.token1.amount === "number" &&
      typeof obj.token1.price === "number" &&
      typeof obj.depositTimestamp === "number" &&
      obj.depositTimestamp > 0
    );
  };

  const handleLoadAndCalculatePerformance = async () => {
    if (!selectedInvestmentFile) {
      setError("Please select an investment file.");
      setCalculatedResults([]);
      setCategorySummaries({});
      return;
    }

    setLoading(true);
    setError(null);
    setCalculatedResults([]); // Clear previous results
    setCategorySummaries({}); // Clear previous summaries

    let parsedInvestments;
    try {
      // Fetch the selected investment file from the public folder
      const response = await fetch(`/investments/${selectedInvestmentFile}`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch ${selectedInvestmentFile}: ${response.statusText}`
        );
      }
      const rawInvestments = await response.json();

      if (!Array.isArray(rawInvestments)) {
        throw new Error(
          `${selectedInvestmentFile} does not contain a JSON array.`
        );
      }

      // Add category based on filename (without .json extension)
      const categoryName = selectedInvestmentFile.replace(".json", "");
      parsedInvestments = rawInvestments.map((inv) => ({
        ...inv,
        category: categoryName,
      }));

      if (!parsedInvestments.every(isValidInvestment)) {
        const firstInvalid = parsedInvestments.find(
          (inv) => !isValidInvestment(inv)
        );
        throw new Error(
          `Invalid investment object structure in ${selectedInvestmentFile}. Check fields. Issue near: ${
            firstInvalid ? JSON.stringify(firstInvalid) : "Unknown"
          }. Remember, 'category' is derived from filename and should not be in the JSON file itself.`
        );
      }
    } catch (e) {
      setError(
        `Error loading or parsing ${selectedInvestmentFile}: ${e.message}`
      );
      setCalculatedResults([]);
      setCategorySummaries({});
      setLoading(false);
      return;
    }

    const individualResults = [];
    const errorMessages = [];

    for (const investment of parsedInvestments) {
      try {
        const result = await calculateSinglePerformance(investment);
        individualResults.push(result);
      } catch (err) {
        console.error(
          `Error processing individual investment ${investment.id}:`,
          err
        );
        errorMessages.push(
          `For Investment ID "${investment.id}": ${err.message}`
        );
      }
    }
    setCalculatedResults(individualResults);

    // --- Generate Category Summaries ---
    const summaries = {};
    individualResults.forEach((result) => {
      const category = result.investmentDetails.category;
      if (!summaries[category]) {
        summaries[category] = {
          totalInitialValue: 0,
          totalCurrentLpValue: 0,
          totalHodlOriginalBasketValue: 0,
          totalHodlToken0Value: 0,
          totalHodlToken1Value: 0,
          earliestDepositTimestampMs: Infinity,
          numInvestments: 0,
          token0Symbols: new Set(), // To collect Token0 symbols
          token1Symbols: new Set(), // To collect Token1 symbols
          aggregatedNetGain: 0,
          aggregatedApr: NaN,
          aggregatedApy: NaN,
          isSubDayCategoryApyExtrapolation: false,
        };
      }
      summaries[category].totalInitialValue += result.initialTotalValueUsd;
      summaries[category].totalCurrentLpValue += result.currentLpValue;
      summaries[category].totalHodlOriginalBasketValue +=
        result.hodlOriginalBasketValue;
      summaries[category].totalHodlToken0Value += result.hodlToken0Value;
      summaries[category].totalHodlToken1Value += result.hodlToken1Value;
      summaries[category].numInvestments += 1;

      // Collect token symbols for the category
      if (
        result.investmentDetails.token0 &&
        result.investmentDetails.token0.symbol
      ) {
        summaries[category].token0Symbols.add(
          result.investmentDetails.token0.symbol
        );
      }
      if (
        result.investmentDetails.token1 &&
        result.investmentDetails.token1.symbol
      ) {
        summaries[category].token1Symbols.add(
          result.investmentDetails.token1.symbol
        );
      }

      const currentDepositMs = result.investmentDetails.depositTimestamp * 1000;
      if (currentDepositMs < summaries[category].earliestDepositTimestampMs) {
        summaries[category].earliestDepositTimestampMs = currentDepositMs;
      }
    });

    // Calculate aggregated metrics for each category
    for (const catName in summaries) {
      const summary = summaries[catName];
      summary.aggregatedNetGain =
        summary.totalCurrentLpValue - summary.totalHodlOriginalBasketValue;

      // Determine display names for Token0 and Token1 for the category
      if (summary.token0Symbols.size === 1) {
        summary.displayToken0Name = `${
          summary.token0Symbols.values().next().value
        }s`;
      } else {
        summary.displayToken0Name = "Token0s (mixed)"; // Or just "Token0s"
      }
      if (summary.token1Symbols.size === 1) {
        summary.displayToken1Name = `${
          summary.token1Symbols.values().next().value
        }s`;
      } else {
        summary.displayToken1Name = "Token1s (mixed)"; // Or just "Token1s"
      }
      // Clean up temporary sets if not needed further
      delete summary.token0Symbols;
      delete summary.token1Symbols;

      const now = Date.now();
      const categoryDurationMs = now - summary.earliestDepositTimestampMs;

      if (categoryDurationMs > 0 && summary.totalInitialValue > 0) {
        const catRateOfReturn =
          summary.totalCurrentLpValue / summary.totalInitialValue - 1;
        const millisecondsInYear = 365.25 * 24 * 60 * 60 * 1000;
        summary.aggregatedApr =
          (catRateOfReturn / categoryDurationMs) * millisecondsInYear * 100;

        const catDays = categoryDurationMs / (1000 * 60 * 60 * 24);
        const catDailyEquivalentRate =
          Math.pow(
            summary.totalCurrentLpValue / summary.totalInitialValue,
            1 / catDays
          ) - 1;

        if (isFinite(catDailyEquivalentRate) && catDailyEquivalentRate > -100) {
          summary.aggregatedApy =
            (Math.pow(1 + catDailyEquivalentRate, 365.25) - 1) * 100;
          if (catDays < 1) {
            summary.isSubDayCategoryApyExtrapolation = true;
          }
        }
      } else if (
        summary.totalInitialValue === 0 &&
        summary.totalCurrentLpValue > 0
      ) {
        summary.aggregatedApr = Infinity;
        summary.aggregatedApy = Infinity;
      }
    }
    setCategorySummaries(summaries);

    if (errorMessages.length > 0) {
      setError(errorMessages.join("\n--- \n"));
    }
    setLoading(false);
  };

  // Helper component for consistent value display
  const ValueDisplay = ({
    label,
    value,
    isCurrency = true,
    isPercentage = false,
    colorClass = "text-gray-800",
    valueColorClass = null,
    extraInfo = null,
    symbol = null,
    isNetGrowth = false,
  }) => {
    let displayValue;
    if (value === Infinity) {
      displayValue = "∞+";
    } else if (isNaN(value) || (isPercentage && !isFinite(value))) {
      displayValue = "N/A";
    } else {
      if (isPercentage) {
        displayValue = `${value.toFixed(2)}%`;
      } else if (isCurrency) {
        displayValue = `$${value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      } else {
        displayValue = `${value.toLocaleString(undefined, {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        })}${symbol ? ` ${symbol}` : ""}`;
      }
    }

    let finalValueColorClass = valueColorClass;
    if (!finalValueColorClass) {
      if (value > 0 && (isCurrency || isPercentage || isNetGrowth)) {
        finalValueColorClass = "text-green-600";
      } else if (value < 0 && (isCurrency || isPercentage || isNetGrowth)) {
        finalValueColorClass = "text-red-600";
      } else {
        finalValueColorClass = "text-gray-900"; // Default for zero or non-applicable positive/negative
      }
    }

    return (
      <p className={`text-sm mb-1 ${colorClass}`}>
        <span className="font-medium">{label}:</span>{" "}
        <strong className={`ml-1 ${finalValueColorClass}`}>
          {displayValue}
        </strong>
        {extraInfo && (
          <em className="text-xs text-gray-500 ml-2">{extraInfo}</em>
        )}
      </p>
    );
  };

  return (
    <div className="bg-gray-100 min-h-screen font-sans p-4 md:p-8 transition-colors duration-300">
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-lg p-6 md:p-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl md:text-4xl font-light text-center text-gray-700 flex-grow">
            Balancer LP Performance Tracker
          </h2>
        </div>

        <div className="mb-8 p-6 bg-slate-50 rounded-lg border border-slate-200">
          <label
            htmlFor="investmentFileSelect"
            className="block mb-2 text-sm font-medium text-gray-700"
          >
            Select Investment File:
          </label>
          <select
            id="investmentFileSelect"
            value={selectedInvestmentFile}
            onChange={(e) => setSelectedInvestmentFile(e.target.value)}
            className="w-full p-3 mb-4 bg-white text-gray-900 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm disabled:bg-gray-200 disabled:cursor-not-allowed"
            disabled={availableInvestmentFiles.length === 0 || loading}
          >
            {availableInvestmentFiles.length === 0 && (
              <option className="text-gray-500">Loading files...</option>
            )}
            {availableInvestmentFiles.map((fileName) => (
              <option
                key={fileName}
                value={fileName}
                className="bg-white text-gray-900"
              >
                {fileName.replace(".json", "")}
              </option>
            ))}
          </select>

          <button
            onClick={handleLoadAndCalculatePerformance}
            disabled={loading || !selectedInvestmentFile}
            className="w-full px-4 py-3 bg-indigo-600 text-white font-semibold rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-150"
          >
            {loading ? "Calculating..." : "Load and Calculate Performance"}
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md whitespace-pre-wrap">
            <strong className="font-bold">Error(s) Occurred:</strong>
            <br />
            {error}
          </div>
        )}

        {Object.keys(categorySummaries)
          .sort()
          .map((categoryName) => {
            const summary = categorySummaries[categoryName];
            const investmentsInCategory = calculatedResults
              .filter((res) => res.investmentDetails.category === categoryName)
              .sort(
                (a, b) =>
                  a.investmentDetails.depositTimestamp -
                  b.investmentDetails.depositTimestamp
              ); // Sort by oldest first

            return (
              <div
                key={categoryName}
                className="mb-10 p-6 bg-sky-50 rounded-xl border border-sky-200 shadow-lg"
              >
                <h3 className="text-2xl font-semibold text-sky-700 border-b-2 border-sky-300 pb-3 mb-6">
                  Category: {categoryName} ( {summary.numInvestments} Pool
                  {summary.numInvestments === 1 ? "" : "s"} )
                </h3>

                {investmentsInCategory.map((result, index) => (
                  <div
                    key={result.investmentDetails.id || index}
                    className="mb-8 p-5 bg-white rounded-lg shadow-md border border-gray-200"
                  >
                    <h4 className="text-xl font-semibold text-indigo-700 mb-3">
                      Pool ID: {result.investmentDetails.id} (
                      <a
                        href={`https://balancer.fi/pools/${result.investmentDetails.chain.toLowerCase()}/v2/${
                          result.investmentDetails.poolAddress
                        }`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-500 hover:text-indigo-600 underline transition-colors"
                      >
                        {result.investmentDetails.poolAddress}
                      </a>
                      )
                    </h4>
                    <details open className="mb-4">
                      <summary className="text-lg font-semibold text-gray-700 cursor-pointer mb-2 hover:text-gray-900">
                        Initial Investment Details
                      </summary>
                      <div className="pl-4 border-l-2 border-gray-200 py-2 space-y-1">
                        <ValueDisplay
                          label={`${
                            result.investmentDetails.token0.symbol || "Token0"
                          } (${result.investmentDetails.token0.address.substring(
                            0,
                            6
                          )}...)`}
                          value={
                            result.investmentDetails.token0.amount *
                            result.investmentDetails.token0.price
                          }
                          extraInfo={`${result.investmentDetails.token0.amount.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 4,
                              maximumFractionDigits: 4,
                            }
                          )} @ $${result.investmentDetails.token0.price.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}`}
                          colorClass="text-gray-700"
                        />
                        <ValueDisplay
                          label={`${
                            result.investmentDetails.token1.symbol || "Token1"
                          } (${result.investmentDetails.token1.address.substring(
                            0,
                            6
                          )}...)`}
                          value={
                            result.investmentDetails.token1.amount *
                            result.investmentDetails.token1.price
                          }
                          extraInfo={`${result.investmentDetails.token1.amount.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 4,
                              maximumFractionDigits: 4,
                            }
                          )} @ $${result.investmentDetails.token1.price.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}`}
                          colorClass="text-gray-700"
                        />
                        <ValueDisplay
                          label="Total Initial Value (Pool)"
                          value={result.initialTotalValueUsd}
                          colorClass="text-gray-700"
                        />
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Deposited At:</span>{" "}
                          {new Date(
                            result.investmentDetails.depositTimestamp * 1000
                          ).toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Age:</span>{" "}
                          {formatDuration(
                            Date.now() -
                              result.investmentDetails.depositTimestamp * 1000
                          )}
                        </p>
                      </div>
                    </details>

                    <div className="mb-4 pt-3 border-t border-gray-200">
                      <h5 className="text-lg font-semibold text-gray-700 mb-2">
                        Current LP Performance (Pool):
                      </h5>
                      <ValueDisplay
                        label="Current LP Value"
                        value={result.currentLpValue}
                        valueColorClass="text-blue-600 font-bold"
                      />
                      <ValueDisplay
                        label="APR"
                        value={result.apr}
                        isPercentage={true}
                      />
                      <ValueDisplay
                        label="APY (daily compounding)"
                        value={result.apy}
                        isPercentage={true}
                        extraInfo={
                          result.isSubDayApyExtrapolation
                            ? "(extrap. from <24h)"
                            : null
                        }
                      />
                      <ValueDisplay
                        label="Net Gain from LP (vs HODL Original Basket)"
                        value={result.netGainFromLp}
                        isNetGrowth={true}
                      />
                    </div>

                    {result.ethSymbolInPool && (
                      <div className="mb-4 pt-3 border-t border-gray-200">
                        <h5 className="text-lg font-semibold text-gray-700 mb-2">
                          {result.ethSymbolInPool} Performance Perspective:
                        </h5>
                        {!isNaN(result.initialLpWorthInEth) &&
                          !isNaN(result.initialEthPriceUsd) && (
                            <ValueDisplay
                              label={`Initial LP Value (in ${
                                result.ethSymbolInPool
                              } at deposit price $${result.initialEthPriceUsd.toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}`}
                              value={result.initialLpWorthInEth}
                              isCurrency={false}
                              symbol={result.ethSymbolInPool}
                            />
                          )}
                        {!isNaN(result.lpUsdValueAsEthAtInitialPrice) &&
                          !isNaN(result.initialEthPriceUsd) && (
                            <>
                              <ValueDisplay
                                label={`Current LP Value (in ${
                                  result.ethSymbolInPool
                                } at deposit price $${result.initialEthPriceUsd.toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}`}
                                value={result.lpUsdValueAsEthAtInitialPrice}
                                isCurrency={false}
                                symbol={result.ethSymbolInPool}
                              />
                              {!isNaN(
                                result.lpValueAsEthAtInitialPrice_pctDiff
                              ) && (
                                <ValueDisplay
                                  label={`└─ Change vs Initial (at deposit price)`}
                                  value={
                                    result.lpValueAsEthAtInitialPrice_pctDiff
                                  }
                                  isPercentage={true}
                                  isNetGrowth={true}
                                />
                              )}
                            </>
                          )}
                        {!isNaN(result.lpUsdValueAsEthAtCurrentPrice) &&
                          !isNaN(result.currentEthPriceUsd) && (
                            <>
                              <ValueDisplay
                                label={`Current LP Value (in ${
                                  result.ethSymbolInPool
                                } at current price $${result.currentEthPriceUsd.toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}`}
                                value={result.lpUsdValueAsEthAtCurrentPrice}
                                isCurrency={false}
                                symbol={result.ethSymbolInPool}
                              />
                              {!isNaN(
                                result.lpValueAsEthAtCurrentPrice_pctDiff
                              ) && (
                                <ValueDisplay
                                  label={`└─ Change vs Initial (at current price)`}
                                  value={
                                    result.lpValueAsEthAtCurrentPrice_pctDiff
                                  }
                                  isPercentage={true}
                                  isNetGrowth={true}
                                />
                              )}
                            </>
                          )}
                        {!isNaN(result.netGrowthInEthToken) && (
                          <>
                            <ValueDisplay
                              label={`Net Growth (in ${result.ethSymbolInPool})`}
                              value={result.netGrowthInEthToken}
                              isCurrency={false}
                              symbol={result.ethSymbolInPool}
                              isNetGrowth={true}
                            />
                            {!isNaN(result.netGrowthInEthToken_pct) && (
                              <ValueDisplay
                                label={`└─ Net Growth % (vs Initial LP in ${result.ethSymbolInPool})`}
                                value={result.netGrowthInEthToken_pct}
                                isPercentage={true}
                                isNetGrowth={true}
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}

                    <div className="mb-4 pt-3 border-t border-gray-200">
                      <h5 className="text-lg font-semibold text-gray-700 mb-2">
                        Strategy Comparison (Pool Values):
                      </h5>
                      {result.allStrategiesCompared.map((strat) => (
                        <div
                          key={strat.name}
                          className={`p-2 rounded-md mb-1 ${
                            strat.name === result.bestStrategy.name
                              ? "bg-green-50 border border-green-200"
                              : "bg-gray-50"
                          }`}
                        >
                          <p className="text-sm">
                            <span className="font-semibold text-gray-800">
                              {strat.name}:
                            </span>{" "}
                            <span className="font-bold text-gray-900">
                              {isNaN(strat.value)
                                ? "N/A"
                                : `$${strat.value.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`}
                            </span>
                            <span className="ml-2 text-xs text-gray-600">
                              (P/L vs Current LP:{" "}
                              <span
                                className={`font-bold ${
                                  isNaN(strat.pnlVsLp)
                                    ? "text-gray-500"
                                    : strat.pnlVsLp > 0
                                    ? "text-green-600"
                                    : strat.pnlVsLp < 0
                                    ? "text-red-600"
                                    : "text-gray-500"
                                }`}
                              >
                                {isNaN(strat.pnlVsLp)
                                  ? "N/A"
                                  : `$${strat.pnlVsLp.toLocaleString(
                                      undefined,
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}`}
                              </span>
                              )
                            </span>
                          </p>
                        </div>
                      ))}
                    </div>

                    <div
                      className={`mt-4 p-3 rounded-md ${
                        result.bestStrategy.name === "LP Position"
                          ? "bg-emerald-50 border border-emerald-300"
                          : "bg-amber-50 border border-amber-300"
                      }`}
                    >
                      <h5
                        className={`text-md font-semibold mb-1 ${
                          result.bestStrategy.name === "LP Position"
                            ? "text-emerald-700"
                            : "text-amber-700"
                        }`}
                      >
                        🏆 Pool Strategy Analysis:
                      </h5>
                      {result.bestStrategy.name === "LP Position" ? (
                        <div className="text-sm text-emerald-600">
                          <p className="font-semibold">
                            LP Position is currently the best for this pool.
                          </p>
                          <p className="mt-1">
                            Outperforming other strategies:
                          </p>
                          <ul className="list-disc list-inside pl-1 text-xs mt-1 space-y-0.5">
                            {result.allStrategiesCompared
                              .filter(
                                (strat) =>
                                  strat.name !== "LP Position" &&
                                  !isNaN(strat.value) &&
                                  !isNaN(result.currentLpValue)
                              )
                              .map((strat) => (
                                <li key={strat.name}>
                                  vs <strong>{strat.name}</strong> by{" "}
                                  <span className="font-bold text-green-700">
                                    $
                                    {(
                                      result.currentLpValue - strat.value
                                    ).toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </li>
                              ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="text-sm text-amber-600">
                          <p>
                            <strong className="font-semibold">
                              {result.bestStrategy.name}
                            </strong>{" "}
                            is best for this pool (Value:{" "}
                            <span className="font-bold text-amber-800">
                              $
                              {result.bestStrategy.value.toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </span>
                            ).
                          </p>
                          <p className="mt-1">
                            This strategy is{" "}
                            <span
                              className={`font-bold ${
                                result.bestStrategy.value -
                                  result.currentLpValue >=
                                0
                                  ? "text-green-700"
                                  : "text-red-700"
                              }`}
                            >
                              $
                              {(
                                result.bestStrategy.value -
                                result.currentLpValue
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>{" "}
                            {result.bestStrategy.value -
                              result.currentLpValue >=
                            0
                              ? "better"
                              : "worse"}{" "}
                            than this pool's LP value.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <div className="mt-8 pt-6 border-t-2 border-dashed border-sky-300">
                  <h4 className="text-xl font-semibold text-sky-700 mb-4">
                    Category Summary: {categoryName}
                  </h4>
                  <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <ValueDisplay
                      label="Total Initial Investment (Category)"
                      value={summary.totalInitialValue}
                    />
                    <ValueDisplay
                      label="Total Current LP Value (Category)"
                      value={summary.totalCurrentLpValue}
                      valueColorClass="text-blue-600 font-bold"
                    />
                    <ValueDisplay
                      label="Aggregated APR (Category)"
                      value={summary.aggregatedApr}
                      isPercentage={true}
                    />
                    <ValueDisplay
                      label="Aggregated APY (Category, daily comp.)"
                      value={summary.aggregatedApy}
                      isPercentage={true}
                      extraInfo={
                        summary.isSubDayCategoryApyExtrapolation
                          ? "(extrap. from <24h overall cat. duration)"
                          : null
                      }
                    />
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h5 className="text-md font-semibold text-gray-700 mb-2">
                      Category LP Performance vs. HODL Strategies:
                    </h5>
                    <p className="text-sm text-gray-600 mb-1">
                      Current Category LP Total:{" "}
                      <strong className="text-gray-900">
                        $
                        {summary.totalCurrentLpValue.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </strong>
                    </p>
                    <div className="pl-4 space-y-1 text-sm">
                      <p>
                        vs. HODLing Original Baskets (Total: $
                        {summary.totalHodlOriginalBasketValue.toLocaleString(
                          undefined,
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )}
                        ):
                        <strong
                          className={`ml-1 ${
                            summary.totalCurrentLpValue -
                              summary.totalHodlOriginalBasketValue >=
                            0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          $
                          {(
                            summary.totalCurrentLpValue -
                            summary.totalHodlOriginalBasketValue
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </strong>
                      </p>
                      <p>
                        vs. HODLing 100% as {summary.displayToken0Name} (Total:
                        $
                        {summary.totalHodlToken0Value.toLocaleString(
                          undefined,
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )}
                        ):
                        <strong
                          className={`ml-1 ${
                            summary.totalCurrentLpValue -
                              summary.totalHodlToken0Value >=
                            0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          $
                          {(
                            summary.totalCurrentLpValue -
                            summary.totalHodlToken0Value
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </strong>
                      </p>
                      <p>
                        vs. HODLing 100% as {summary.displayToken1Name} (Total:
                        $
                        {summary.totalHodlToken1Value.toLocaleString(
                          undefined,
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )}
                        ):
                        <strong
                          className={`ml-1 ${
                            summary.totalCurrentLpValue -
                              summary.totalHodlToken1Value >=
                            0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          $
                          {(
                            summary.totalCurrentLpValue -
                            summary.totalHodlToken1Value
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default LpPerformanceTracker;
